import {
  autoUpdate,
  flip,
  FloatingFocusManager,
  offset,
  shift,
  useFloating,
  useInteractions,
  useRole,
} from '@floating-ui/react'

import CopySvg from '@/assets/icons/copy.svg?react'

import style from './CopyableText.module.scss'

async function copyText(text: string, onSuccess?: () => void, onFailure?: (e: Error) => void) {
  try {
    const clipboardItemInput = new ClipboardItem({
      'text/plain': new Blob([text], {type: 'text/plain'}),
    })
    await navigator.clipboard.write([clipboardItemInput])
    onSuccess?.()
  } catch (e) {
    onFailure?.(e as Error)
  }
}

type Props = {
  label: string
  text: string
  buttonProps?: Omit<ComponentProps<'button'>, 'ref' | 'onClick'>
  iconProps?: ComponentProps<'svg'>
} & ComponentProps<'div'>

const CopyableText = deepMemo<PropsWithChildren<Props>>(function CopyableText({
  label,
  text,
  buttonProps,
  iconProps,
  children,
  ...props
}) {
  const latestText = useLatest(text)
  const [isCopied, setIsCopied] = useReducer((_: boolean, next: boolean) => {
    if (next) {
      setTimeout(() => {
        setIsCopied(false)
      }, 2000)
    }
    return next
  }, false)

  const {refs, floatingStyles, context} = useFloating({
    strategy: 'absolute',
    open: isCopied,
    onOpenChange: setIsCopied,
    middleware: [offset(5), flip({fallbackAxisSideDirection: 'end'}), shift()],
    whileElementsMounted: autoUpdate,
  })

  const role = useRole(context)

  const {getReferenceProps, getFloatingProps} = useInteractions([role])

  const copy = useCallback(() => {
    void copyText(
      latestText.current,
      () => {
        setIsCopied(true)
      },
      e => {
        console.error('Copy failed', e)
      },
    )
  }, [])

  return (
    <div {...props}>
      {children ?? text}
      <button
        aria-label={`Copy ${label}`}
        {...buttonProps}
        className={style.CopyButton}
        ref={refs.setReference}
        {...getReferenceProps({
          'aria-expanded': undefined,
          'aria-haspopup': undefined,
          'aria-controls': undefined,
        })}
        onClick={copy}
      >
        <CopySvg aria-hidden {...iconProps} />
        {isCopied && (
          <FloatingFocusManager context={context} modal={false}>
            <div
              className={style.Popover}
              ref={refs.setFloating}
              style={floatingStyles}
              {...getFloatingProps()}
            >
              <div className={style.PopoverArrow} />
              <div className={style.PopoverContent}>Copied!</div>
            </div>
          </FloatingFocusManager>
        )}
      </button>
    </div>
  )
})()

export default CopyableText
